// https://elevenlabs.io/docs/models#models-overview
export enum AudioModel {
  ElevenMultilingualV2 = 'eleven_multilingual_v2',
  ElevenFlashV25 = 'eleven_flash_v2_5',
  ElevenFlashV2 = 'eleven_flash_v2',
  ElevenTurboV25 = 'eleven_turbo_v2_5',
  ElevenTurboV2 = 'eleven_turbo_v2',
  ElevenMonolingualV1 = 'eleven_monolingual_v1',
}

// Need to be synced with backend/flow/api/src/models/voiceCall.ts
export enum VoiceCallLanguage {
  Bulgarian = 'bg',
  Chinese = 'zh',
  Czech = 'cs',
  Danish = 'da',
  Dutch = 'nl',
  English = 'en',
  Finnish = 'fi',
  French = 'fr',
  German = 'de',
  Hindi = 'hi',
  Hungarian = 'hu',
  Indonesian = 'id',
  Italian = 'it',
  Japanese = 'ja',
  Korean = 'ko',
  Malay = 'ms',
  Multilingual = 'multi',
  Polish = 'pl',
  Portuguese = 'pt',
  Romanian = 'ro',
  Russian = 'ru',
  Slovak = 'sk',
  Spanish = 'es',
  Swedish = 'sv',
  Tamil = 'ta',
  Turkish = 'tr',
  Ukrainian = 'uk',
  Vietnamese = 'vi',
}

/**
 * Voice provider identifiers used by Vapi.ai.
 * Values must match Vapi's API requirements exactly (lowercase format).
 * @see https://docs.vapi.ai/api-reference/assistants/create-assistant#request.body.voice.provider
 */
export enum VoiceProvider {
  ElevenLabs = '11labs',
  OpenAi = 'openai',
  Cartesia = 'cartesia',
}

/**
 * ElevenLabs voice persona identifiers.
 * Compatible with ElevenLabs voice provider only.
 * @see https://elevenlabs.io/docs/voices/premade-voices
 * @see https://dashboard.vapi.ai/library/voice
 */
export enum ElevenLabsVoicePersona {
  Alice = 'Xb7hH8MSUJpSbSDYk0k2',
  Arabella = 'Z3R5wn05IrDiVCyEkUrK',
  Arnold = 'VR6AewLTigWG4xSOukaG',
  Bill = 'pqHfZKP75CvOlQylNhV4',
  Brian = 'nPczCjzI2devNBz1zQrb',
  Carolina = 'UOIqAnmS11Reiei1Ytkc',
  Charlie = 'IKne3meq5aSn9XLyUdCD',
  Dakota = 'P7x743VjyZEOihNNygQ9',
  Daniel = 'onwK4e9ZLuTAKqWW03F9',
  Dorothy = 'ThT5KcBeYPX3keUQqHPh',
  Eduardo = 'W6Z2FAa578IKOGSVo2sA',
  Eric = 'cjVigY5qzO86Huf0OWal',
  Gabriela = 'hHjbwzYZW17oh0p05AKv',
  Javier = 'CAEve7xpu0AvVWiKm2px',
  Jessa = 'yj30vwTGJxSHezdAGsv9',
  Jessica = 'cgSgspJ2msm6clMCkdW9',
  Josh = 'TxGEqnHWrfWFTfGW9XjX',
  Kelli = 'vvRU52fIIRzylmIUcoKl',
  Liam = 'TX3LPaxmHKxFdv7VOQHJ',
  Maribeth = 'JEAgwU0JZFGxl2KjC3if',
  Matthew = 'Yko7PKHZNXotIFUBG7I9',
  Nicole = 'piTKgcLEGmPE4e6mEKli',
  Paul = '5Q0t7uMcjvnagumLfvZi',
  Rachel = '21m00Tcm4TlvDq8ikWAM',
  Rhett = 'D4xQvkd2SmpDZJ8sEwvA',
  Sarah = 'sarah',
  Serena = 'pMsXgVXv3BLzUgSXRplE',
}

/**
 * OpenAI standard voice persona identifiers.
 * Compatible with all OpenAI models (both realtime and non-realtime).
 * @see https://platform.openai.com/docs/guides/realtime-voice
 */
export enum OpenAiStandardVoicePersona {
  Alloy = 'alloy',
  Echo = 'echo',
  Shimmer = 'shimmer',
}

/**
 * OpenAI realtime-exclusive voice persona identifiers.
 * Only compatible with OpenAI realtime models (gpt-4o-realtime, gpt-4o-mini-realtime, etc).
 * @see https://platform.openai.com/docs/guides/realtime-voice
 */
export enum OpenAiRealtimeVoicePersona {
  Marin = 'marin',
  Cedar = 'cedar',
}

/**
 * Cartesia voice persona identifiers.
 * Compatible with Cartesia voice provider only.
 * @see https://docs.vapi.ai/providers/voice/cartesia
 */
export enum CartesiaVoicePersona {
  CustomerSupportLady = '829ccd10-f8b3-43cd-b8a0-4aeaa81f3b30',
  Ariana = 'ec1e269e-9ca0-402f-8a18-58e0e022355a',
  MidWesternWomen = '11af83e2-23eb-452f-956e-7fee218ccb5c',
  Sarah = '694f9389-aac1-45b6-b726-9d9369183238',
}

export type VoicePersona =
  | ElevenLabsVoicePersona
  | OpenAiStandardVoicePersona
  | OpenAiRealtimeVoicePersona
  | CartesiaVoicePersona;

export enum VoiceCallModel {
  // OpenAI models
  gpt_4o = 'gpt-4o',
  gpt_4o_2024_11_20 = 'gpt-4o-2024-11-20',
  gpt_4o_mini = 'gpt-4o-mini',
  gpt_4o_realtime_cluster = 'gpt-realtime-2025-08-28',
  gpt_4o_realtime_preview = 'gpt-4o-realtime-preview-2024-12-17',
  gpt_4o_mini_realtime_preview = 'gpt-4o-mini-realtime-preview-2024-12-17',
  gpt_4_1 = 'gpt-4.1',
  gpt_4_1_mini = 'gpt-4.1-mini',
  gpt_5 = 'gpt-5',
  gpt_5_mini = 'gpt-5-mini',
  gpt_5_nano = 'gpt-5-nano',
  gpt_5_chat = 'gpt-5-chat-latest',
  gpt_5_1 = 'gpt-5.1',
  gpt_5_2 = 'gpt-5.2',
  gpt_5_1_chat = 'gpt-5.1-chat-latest',
  gpt_5_2_chat = 'gpt-5.2-chat-latest',
  // Anthropic models
  claude_3_5_sonnet = 'claude-3-5-sonnet-20240620',
  claude_3_7_sonnet = 'claude-3-7-sonnet-20250219',
  claude_opus_4 = 'claude-opus-4-20250514',
  claude_opus_4_5 = 'claude-opus-4-5-20251101',
  claude_opus_4_6 = 'claude-opus-4-6',
  claude_sonnet_4 = 'claude-sonnet-4-20250514',
  claude_sonnet_4_5 = 'claude-sonnet-4-5-20250929',
  claude_sonnet_4_6 = 'claude-sonnet-4-6',
  claude_haiku_4_5 = 'claude-haiku-4-5-20251001',
  // Notable proxy
  notable_gpt_4o = 'notable-gpt-4o',
}

export const VoiceCallModelDescriptions: Record<VoiceCallModel, string> = {
  [VoiceCallModel.gpt_4o]: 'GPT-4o (deprecated, use gpt-4o-2024-11-20 instead)',
  [VoiceCallModel.gpt_4o_mini]: 'GPT-4o-mini',
  [VoiceCallModel.gpt_4o_2024_11_20]: 'GPT-4o-2024-11-20',
  [VoiceCallModel.gpt_4o_realtime_cluster]: 'GPT-4o Realtime (Production)',
  [VoiceCallModel.gpt_4o_realtime_preview]: 'GPT-4o Realtime (Preview)',
  [VoiceCallModel.gpt_4o_mini_realtime_preview]: 'GPT-4o-mini Realtime (Preview)',
  [VoiceCallModel.gpt_4_1]: 'GPT-4.1',
  [VoiceCallModel.gpt_4_1_mini]: 'GPT-4.1-mini',
  [VoiceCallModel.claude_3_5_sonnet]: 'Claude 3.5 Sonnet',
  [VoiceCallModel.claude_3_7_sonnet]: 'Claude 3.7 Sonnet',
  [VoiceCallModel.claude_opus_4]: 'Claude Opus 4',
  [VoiceCallModel.claude_opus_4_5]: 'Claude Opus 4.5',
  [VoiceCallModel.claude_opus_4_6]: 'Claude Opus 4.6',
  [VoiceCallModel.claude_sonnet_4]: 'Claude Sonnet 4',
  [VoiceCallModel.claude_sonnet_4_5]: 'Claude Sonnet 4.5',
  [VoiceCallModel.claude_sonnet_4_6]: 'Claude Sonnet 4.6',
  [VoiceCallModel.claude_haiku_4_5]: 'Claude Haiku 4.5',
  [VoiceCallModel.notable_gpt_4o]: 'GPT-4o (Notable)',
  [VoiceCallModel.gpt_5]: 'GPT-5',
  [VoiceCallModel.gpt_5_mini]: 'GPT-5-mini',
  [VoiceCallModel.gpt_5_nano]: 'GPT-5-nano',
  [VoiceCallModel.gpt_5_chat]: 'GPT-5-chat',
  [VoiceCallModel.gpt_5_1]: 'GPT-5.1',
  [VoiceCallModel.gpt_5_1_chat]: 'GPT-5.1-chat',
  [VoiceCallModel.gpt_5_2]: 'GPT-5.2',
  [VoiceCallModel.gpt_5_2_chat]: 'GPT-5.2-chat',
};

export enum VoiceCallBackgroundSound {
  Off = 'off',
  Office = 'office',
}

export enum VoiceCallFirstMessageMode {
  AssistantSpeaksFirst = 'assistant-speaks-first',
  AssistantSpeaksFirstWithModelGeneratedMessage = 'assistant-speaks-first-with-model-generated-message',
  AssistantWaitsForUser = 'assistant-waits-for-user',
}

// https://docs.vapi.ai/api-reference/webhooks/server-message#request.body.message.EndOfCallReport.artifact.messagesOpenAIFormatted.role
export enum OpenAiMessageRole {
  System = 'system',
  Assistant = 'assistant',
  User = 'user',
}

// https://docs.vapi.ai/api-reference/webhooks/server-message#request.body.message.EndOfCallReport.artifact.messages
export enum VapiArtifactMessageRole {
  System = 'system',
  User = 'user',
  Bot = 'bot',
  ToolCall = 'tool_calls',
  ToolCallResult = 'tool_call_result',
}

export enum VoiceCallOptionMode {
  Enabled = 'Enabled',
  Disabled = 'Disabled',
}

export enum VoiceCallIdleMessage {
  AreYouStillThere = 'Are you still there?',
  IsThereAnythingElseYouNeedHelpWith = 'Is there anything else you need help with?',
  FeelFreeToAskMeAnyQuestions = 'Feel free to ask me any questions.',
  HowCanIAssistYouFurther = 'How can I assist you further?',
  LetMeKnowIfThereIsAnythingYouNeed = "Let me know if there's anything you need.",
  ImStillHereIfYouNeedAssistance = "I'm still here if you need assistance.",
  ImReadyToHelpWheneverYouAre = "I'm ready to help whenever you are.",
  IsThereSomethingSpecificYouAreLookingFor = "Is there something specific you're looking for?",
  ImHereToHelpWithAnyQuestionsYouHave = "I'm here to help with any questions you have.",
}

export const OPENAI_REALTIME_MODELS = [
  VoiceCallModel.gpt_4o_realtime_cluster,
  VoiceCallModel.gpt_4o_realtime_preview,
  VoiceCallModel.gpt_4o_mini_realtime_preview,
] as const;

export const OPENAI_REALTIME_COMPATIBLE_VOICES = [
  ...Object.values(OpenAiStandardVoicePersona),
  ...Object.values(OpenAiRealtimeVoicePersona),
] as const;

export function isRealTimeModel(
  model: VoiceCallModel
): model is
  | VoiceCallModel.gpt_4o_realtime_cluster
  | VoiceCallModel.gpt_4o_realtime_preview
  | VoiceCallModel.gpt_4o_mini_realtime_preview {
  return (OPENAI_REALTIME_MODELS as readonly VoiceCallModel[]).includes(model);
}

export function isElevenLabsCompatibleVoicePersona(
  persona: VoicePersona
): persona is ElevenLabsVoicePersona {
  return Object.values(ElevenLabsVoicePersona).includes(persona as ElevenLabsVoicePersona);
}

export function isRealtimeCompatibleVoicePersona(
  persona: VoicePersona
): persona is OpenAiStandardVoicePersona | OpenAiRealtimeVoicePersona {
  return (
    Object.values(OpenAiStandardVoicePersona).includes(persona as OpenAiStandardVoicePersona) ||
    isRealTimeExclusiveVoicePersona(persona)
  );
}

export function isRealTimeExclusiveVoicePersona(
  persona: VoicePersona
): persona is OpenAiRealtimeVoicePersona {
  return Object.values(OpenAiRealtimeVoicePersona).includes(persona as OpenAiRealtimeVoicePersona);
}

export function isCartesiaCompatibleVoicePersona(
  persona: VoicePersona
): persona is CartesiaVoicePersona {
  return Object.values(CartesiaVoicePersona).includes(persona as CartesiaVoicePersona);
}
